//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { Enums } from '../../models/common'
import { OpData, OpLocation, OpArticle } from '../../models/core'

import { ColumnModel } from '../../modules/api/api.models'
import { LocalizationHelper } from '../../helpers'

import { DepositoryService, DEPOSITORY_SERVICE_NAME } from './depository.service'
import { GridService, GRID_SERVICE_NAME } from '../grid'
import { ReferenceTypeService, REFERENCE_TYPE_SERVICE_NAME } from '../reference-type'


//#region Public Types

export const DEPOSITORY_MIN_STATES_SERVICE_NAME = 'DepositoryMinStateService'
export const DEPOSITORY_MIN_STATES_SERVICE_PROVIDER_NAME = 'DepositoryMinStateServiceProvider'

export interface MinStateItem {
    amount: number,
    id: number,
    name: string,

    toString(): string
}

//#endregion


export class DepositoryMinStatesService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Depository.MinStates]' }

    //#endregion


    //#region Ctor

    constructor(private depositoryService: DepositoryService, private gridService: GridService, private referenceTypeService: ReferenceTypeService) { }

    //#endregion


    //#region Columns

    public getDepositoryMinStatesGridColumns(): ColumnModel[] {
        const amountColumn = new ColumnModel()

        amountColumn.idName = 'amount'
        amountColumn.name = 'amount'
        amountColumn.type = Enums.DataTypeClass.Integer
        amountColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Amount)

        const nameColumn = this.gridService.getNameColumn()

        nameColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Type)

        return [nameColumn, amountColumn]
    }

    public getDepositoryMinStatesGridEditColumns(): ColumnModel[] {
        const column: ColumnModel = this.gridService.getNameColumn()
        column.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Type)
        return [column]
    }

    public getEditIconInCell(options: any, dataField: string): void {
        this.gridService.getEditIconInCell(options, dataField)
    }

    //#endregion


    //#region GridEdit

    public getArticlesGridEditData(): JQueryPromise<any[]> {
        return this.referenceTypeService
            .getDictionaryReferenceObjectList(Enums.ReferenceType.IdArticle)
            .then((articles: OpArticle[]) => {
                return _.map(articles, (article: OpArticle) => {
                    const gridObject: any = this.gridService.gridObjectFromIReferenceType(article)

                    gridObject.amount = 0
                    gridObject.toString = () => { return gridObject.name }

                    return gridObject
                })
            })
    }

    public saveGridEditResult(depository: OpLocation, added: any[], removed: any[], grid: any): JQueryPromise<OpLocation> {
        const depositoryToSave = new OpLocation(null)

        depositoryToSave.idLocation = depository.idLocation
        depositoryToSave.opState = Enums.OpChangeState.Modified

        let nextDataIndex: number = depository.dataList.getNextIndex(Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_TYPE)

        _.forEach(added, (added: any) => {
            const addedGridObject: any = _.find(grid, (gridObject: any) => gridObject.id === added.id)

            if (addedGridObject != null) {
                depositoryToSave.dataList.setValue(addedGridObject.id, Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_TYPE, nextDataIndex, 0)
                depositoryToSave.dataList.setValue(addedGridObject.amount, Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_AMOUNT, nextDataIndex, 0)

                nextDataIndex += 1
            }
        })

        _.forEach(grid, (gridItem: any) => {
            if (gridItem.isEdited) {
                const typeDataList: OpData[] = depository.dataList.getDataList(Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_TYPE)
                const typeDataToEdit: OpData = _.find(typeDataList, (data: OpData) => data.value === gridItem.id)

                if (typeDataToEdit != null) {
                    const amountDataToEdit: OpData = depository.dataList.getData(Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_AMOUNT, typeDataToEdit.indexNBR)

                    depositoryToSave.dataList.setValue(typeDataToEdit.value, typeDataToEdit.idDataType, typeDataToEdit.indexNBR, typeDataToEdit.idData, Enums.OpChangeState.Modified)

                    if (amountDataToEdit != null) {
                        depositoryToSave.dataList.setValue(gridItem.amount, amountDataToEdit.idDataType, amountDataToEdit.indexNBR, amountDataToEdit.idData, Enums.OpChangeState.Modified)
                    }
                }
            }
        })

        _.forEach(removed, (removed: any) => {
            const removedGridObject: any = _.find(grid, (gridObject: any) => gridObject.id === removed.id)

            if (removedGridObject != null) {
                const typeDataList: OpData[] = depository.dataList.getDataList(Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_TYPE)
                const typeDataToRemove: OpData = _.find(typeDataList, (data: OpData) => data.value === removedGridObject.id)

                if (typeDataToRemove != null) {
                    const amountDataToRemove: OpData = depository.dataList.getData(Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_AMOUNT, typeDataToRemove.indexNBR)

                    depositoryToSave.dataList.setValue(typeDataToRemove.value, typeDataToRemove.idDataType, typeDataToRemove.indexNBR, typeDataToRemove.idData, Enums.OpChangeState.Delete)

                    if (amountDataToRemove != null) {
                        depositoryToSave.dataList.setValue(amountDataToRemove.value, amountDataToRemove.idDataType, amountDataToRemove.indexNBR, amountDataToRemove.idData, Enums.OpChangeState.Delete)
                    }
                }
            }
        })

        return this.depositoryService.saveDepository(depositoryToSave)
    }

    //#endregion


    //#region MinStateItem

    public createMinStateItems(depository: OpLocation): JQueryPromise<MinStateItem[]> {
        if (depository != null) {
            return this.referenceTypeService
                .getDictionaryReferenceObjectList(Enums.ReferenceType.IdArticle)
                .then((articles: OpArticle[]) => {
                    const elementTypeData: OpData[] = depository.dataList.getDataList(Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_TYPE)
                    const elementAmountData: OpData[] = depository.dataList.getDataList(Enums.DataType.LOCATION_DEPOSITORY_MIN_STATE_ELEMENT_AMOUNT)

                    if (elementTypeData != null && elementAmountData != null) {
                        return _
                            .zip(elementTypeData, elementAmountData)
                            .filter((pair: [OpData, OpData]) => {
                                const [idArticleData, amountData] = pair

                                return idArticleData != null && amountData != null
                            })
                            .map((pair: [OpData, OpData]) => {
                                const [idArticleData, amountData] = pair

                                const article: OpArticle = _.find(articles, (article: OpArticle) => article.idArticle === idArticleData.value)

                                return {
                                    name: article != null ? article.toString() : null,
                                    amount: amountData.value,
                                    id: idArticleData.value,
                                    toString() {
                                        return this.name
                                    }
                                }
                            })
                    }

                    return []
                })

        }

        return $.Deferred<MinStateItem[]>().resolve([]).promise()
    }

    //#endregion
}


export class DepositoryMinStatesServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        DEPOSITORY_SERVICE_NAME,
        GRID_SERVICE_NAME,
        REFERENCE_TYPE_SERVICE_NAME,
        function (
            depositoryService: DepositoryService,
            gridService: GridService,
            referenceTypeService: ReferenceTypeService
        ) {
            return new DepositoryMinStatesService(depositoryService, gridService, referenceTypeService);
        }]

    //#endregion
}  